import { widgetCodeSchema } from '@framework/engine/schema/widgets/validation/widgetCodeSchema.tsx';
import { yup } from '@framework/validation/initYup.ts';
import { type ObjectSchema } from 'yup';
import {
  type IMapLayer,
  isCompetenceCapableLayerType,
  isIntersectableLayerType,
  isQueryableLayerType,
  isVectorLayerType,
  MapLayerType,
  MapSymbolShape,
} from '../MapLayer.ts';
import { MapLayerCompetenceSchema } from './MapLayerCompetenceSchema.ts';
import { MapLayerIdentifySchema } from './MapLayerIdentifySchema.ts';
import { MapLayerIntersectionSchema } from './MapLayerIntersectionSchema.ts';
import { MapLayerWfsSchema } from './MapLayerWfsSchema.ts';

export const MapLayerSchema: ObjectSchema<IMapLayer> = yup
  .object({
    type: yup.string().required().oneOfEnum(MapLayerType).label('Tipo'),
    code: widgetCodeSchema,
    name: yup.string().required().label('Nome'),
    url: yup.string().required().label('URL'),
    minZoom: yup.number().min(0).positive().integer().label('Zoom Minimo'),
    labelPropertyName: yup.string().notRequired().label('Etichetta Geometria'),
    group: yup.string().notRequired().nullable().label('Gruppo'),
    style: yup
      .object({
        fillColor: yup
          .string()
          .required()
          .label('Colore di riempimento')
          .parentWhen(['^.type'], ([type], schema) =>
            isVectorLayerType(type)
              ? schema.required()
              : schema.notRequired().strip(),
          ),
        strokeColor: yup
          .string()
          .required()
          .label('Colore del bordo')
          .parentWhen(['^.type'], ([type], schema) =>
            isVectorLayerType(type)
              ? schema.required()
              : schema.notRequired().strip(),
          ),
        strokeWidth: yup
          .number()
          .integer()
          .min(0)
          .required()
          .label('Spessore del bordo')
          .parentWhen(['^.type'], ([type], schema) =>
            isVectorLayerType(type)
              ? schema.required()
              : schema.notRequired().strip(),
          ),
        opacity: yup
          .number()
          .min(0)
          .max(1)
          .default(1)
          .required()
          .label('Opacità'),
        // Opzionale anche per i layer vettoriali: se assente, il runtime
        // applica il simbolo di default (cerchio)
        symbol: yup
          .object({
            shape: yup
              .string()
              .required()
              .oneOfEnum(MapSymbolShape)
              .label('Simbolo'),
            // Lo slider in UI emette null al clear del valore
            size: yup
              .number()
              .integer()
              .min(2)
              .max(20)
              .allowNullable()
              .label('Dimensione simbolo'),
          })
          .notRequired()
          .default(undefined)
          // Il clear della select in UI lascia `{ shape: null }`: un
          // simbolo senza shape equivale a non configurato
          .transform(value => (value?.shape ? value : undefined))
          .parentWhen(['^.type'], ([type], schema) =>
            isVectorLayerType(type) ? schema : schema.strip(),
          ),
      })
      .addChildrenParentDeps()
      .required(),
    intersection: MapLayerIntersectionSchema.when(['type'], ([type], schema) =>
      isIntersectableLayerType(type)
        ? schema.notRequired()
        : schema.notRequired().strip(),
    ),
    identify: MapLayerIdentifySchema.when(['type'], ([type], schema) =>
      isQueryableLayerType(type)
        ? schema.notRequired()
        : schema.notRequired().strip(),
    ),
    competence: MapLayerCompetenceSchema.when(['type'], ([type], schema) =>
      isCompetenceCapableLayerType(type)
        ? schema.notRequired()
        : schema.notRequired().strip(),
    ),
    // A differenza degli altri sotto-oggetti, `wfs` è obbligatorio per i
    // layer WFS: senza typeName il layer non è utilizzabile
    wfs: MapLayerWfsSchema.when(['type'], ([type], schema) =>
      type === MapLayerType.WFS
        ? schema.required()
        : schema.notRequired().strip(),
    ),
  })
  .noUnknown()
  .required();
